import { api } from './api';

// --- TYPES ---

export interface Zone {
    id: number;
    name: string;
    city?: string;
    state?: string;
    isActive: boolean;
    surgeMultiplier: number;
    rushHourStart: number | null;
    rushHourEnd: number | null;
    rushHourMultiplier: number | null;
    geocodeProvider?: string | null;
    placesProvider?: string | null;
    routeProvider?: string | null;
    createdAt: string;
    updatedAt: string;
    _count?: {
        zoneRates: number;
        subscriptionOverrides: number;
    };
}

export interface ZoneRateItem {
    rideOptionId: number;
    rideType: string;
    rideName: string;
    globalBaseFare: number;
    globalPerKm: number;
    overrideId: number | null;
    baseFare: number | null;
    perKm: number | null;
    pickupCharges: number | null;
    isOverridden: boolean;
}

export interface ZoneSubscriptionPlan {
    planId: number;
    name: string;
    globalPrice: number;
    globalMaxRides: number | null;
    interval: string;
    isOverridden: boolean;
    overridePrice: number | null;
    isAvailable: boolean;
    overrideMaxRides: number | null;
}

export interface LocationNode {
    state: string;
    cities: string[];
}

// --- API METHODS ---

export const getZones = (params?: { city?: string; state?: string }) =>
    api.get<Zone[]>('/admin/zones', { params });

export const getZoneById = (id: number) => api.get<Zone>(`/admin/zones/${id}`);

export const getZoneHierarchy = () =>
    api.get<LocationNode[]>('/admin/zones/hierarchy');

export const updateZone = (id: number, data: Partial<Zone> & { lat?: number; lng?: number; size?: number }) =>
    api.patch(`/admin/zones/${id}`, data);

export const getZoneRates = (zoneId: number) =>
    api.get<ZoneRateItem[]>(`/admin/zones/${zoneId}/rates`);

export const upsertZoneRate = (zoneId: number, data: {
    rideOptionId: number;
    baseFare?: number;
    perKm?: number;
    pickupCharges?: number;
    minKm?: number;
}) => api.post(`/admin/zones/${zoneId}/rates`, data);

export const createZone = (data: {
    name: string;
    city: string;
    state: string;
    lat: number;
    lng: number;
    size: number;
    surgeMultiplier: number;
    rushHourMultiplier: number;
    rushHourStart: number;
    rushHourEnd: number;
    extraBaseFare: number;
    extraPerKm: number;
    geocodeProvider?: string | null;
    placesProvider?: string | null;
    routeProvider?: string | null;
}) => api.post<Zone>('/admin/zones', data);

export const renameCity = (data: { oldName: string; newName: string; state: string }) =>
    api.post('/admin/zones/rename-city', data);

export const getZoneSubscriptionPlans = (zoneId: number) =>
    api.get<ZoneSubscriptionPlan[]>(`/admin/zones/${zoneId}/subscription-plans`);

export const upsertZoneSubscriptionOverride = (zoneId: number, data: {
    planId: number;
    price?: number | string | null;
    isAvailable?: boolean;
    maxRides?: number | string | null;
}) => api.post(`/admin/zones/${zoneId}/subscription-plans`, data);